import { useNavigate, useParams } from "react-router-dom";
import { useModalStore } from "../../store/useModalStore";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDeviceDetail } from "../../lib/apis/hooks/device.hook";
import type { createDeviceForm } from "../../lib/apis/dtos/request/device.request";
import { deleteDeviceColor, patchDevice, postDeviceColor } from "../../lib/apis/v1/device.service";
import { getQueryClient } from "../../config/queryClient.config";
import FailSnackbar from "../../components/ui/snackbar/fail.component";
import SuccessSnackbar from "../../components/ui/snackbar/success.component";
import ExplainTitle from "../../components/layout/header/explain-title";
import { Checkbox, FormControlLabel, FormGroup, Switch } from "@mui/material";
import { MODAL_KEY } from "../../lib/constants/modal.constants";
import { formatDate } from "../../utils/date.utils";
import Button from "../../components/ui/button";

const CATEGORY_MENU = {
  IPHONE: "아이폰",
  GALAXY: "갤럭시",
};

const CATEGORY_TYPE = [
  { label: "주요폰", value: "main" },
  { label: "중저가폰", value: "low" },
  { label: "키즈폰", value: "kids" },
];

export default function ModelDetailPage() {
  const navigate = useNavigate();
  const { openModal } = useModalStore();
  const { id } = useParams();

  const parsedId =
    typeof id === "string" ? id : Array.isArray(id) ? id[0] : "0";

  const { data, isSuccess } = useQuery({
    ...useDeviceDetail(parsedId),
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, form }: { id: string; form: createDeviceForm }) =>
      patchDevice(id, { ...form, deviceCode: undefined }),
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: ["device-detail"] });
    },
  });

  const postColorMutation = useMutation({
    mutationFn: ({ id, colorName }: { id: string | number; colorName: string }) =>
      postDeviceColor(id, colorName),
    onSuccess: (res) => {
      const { error } = res || {};
      if (error) {
        FailSnackbar("색상 옵션 추가 실패");
      } else {
        SuccessSnackbar("색상 옵션 추가 완료")
        getQueryClient().invalidateQueries({ queryKey: ["device-detail"] });
      }
    },
    onError: () => {
      FailSnackbar("색상 옵션 추가 실패");
    }
  })

  const deleteColorMutation = useMutation({
    mutationFn: ({ id, colorName }: { id: string | number; colorName: string }) =>
      deleteDeviceColor(id, colorName),
    onSuccess: (_) => {
      getQueryClient().invalidateQueries({ queryKey: ["device-detail"] });
      SuccessSnackbar("색상 옵션 삭제 완료")
    },
    onError: () => {
      FailSnackbar("색상 옵션 삭제 실패")
    }
  })

  const { result } = data || {};

  return (
    <div className="p-6">
      <ExplainTitle
        title="기종 관리"
        explanation="기종 정보를 관리하세요."
        backbutton={true}
        backOnclick={() => {
          getQueryClient().invalidateQueries({ queryKey: ["device-list"] });
          navigate("/model");
        }}
      />
      <table className="w-full">
        <tbody>
          <tr className="content-table-tr">
            <td className="content-table-th">모델 고유 번호</td>
            <td className="content-table-td" colSpan={3}>
              {result?.deviceCode || "-"}
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">카테고리</td>
            <td className="content-table-td">
              <FormGroup className="px-[9px]" row>
                {isSuccess &&
                  CATEGORY_TYPE.map(({ label, value }, index) => {
                    const key = `${value}Check` as keyof typeof result;
                    return (
                      <FormControlLabel
                        key={index}
                        control={
                          <Checkbox
                            disableFocusRipple
                            checked={result?.[key]}
                            onChange={() => {
                              const _value = !result?.[key] as boolean;
                              const _result = {
                                id: parsedId,
                                form: {
                                  ...result,
                                  [key]: _value,
                                } as createDeviceForm,
                              };
                              patchMutation.mutate(_result);
                            }}
                          />
                        }
                        label={label}
                      />
                    );
                  })}
              </FormGroup>
            </td>
            <td className="content-table-th">모델 구분</td>
            <td className="content-table-td">
              {result?.category
                ? CATEGORY_MENU[result.category as keyof typeof CATEGORY_MENU]
                : ""}
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">기종명</td>
            <td className="content-table-td flex-row" colSpan={3}>
              {result?.deviceName || "-"}
              <img
                onClick={() => {
                  {
                    openModal(MODAL_KEY.TextField, {
                      title: "기종명",
                      value: result?.deviceName || "",
                      click: (change: string) => patchMutation.mutate({
                        id: parsedId,
                        form: {
                          ...result,
                          deviceName: change,
                        } as createDeviceForm,
                      }),
                    });
                  }
                }}
                src="/static/icons/icon_pencil_blue.svg"
                width={24}
                height={24}
                className="ml-[8px] cursor-pointer"
                alt={""}
              />
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">용량(GB)</td>
            <td className="content-table-td" colSpan={3}>
              <div className="flex-row gap-2">
                {result?.sizeAvailable.sort().map((item, index) => (
                  <div className="border px-2 py-1 rounded-[4px]" key={index}>{item}</div>
                ))}
              </div>
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">생성일</td>
            <td className="content-table-td" colSpan={3}>
              {formatDate(result?.createdAt || "", 'YYYY-MM-DD HH:mm:ss') || "-"}
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">시세표 기본 포함</td>
            <td className="content-table-td" colSpan={3}>
              <Switch
                checked={result?.isDefault}
                onClick={(_e) => {
                  const _result = { ...result, isDefault: !result?.isDefault } as createDeviceForm
                  patchMutation.mutate({
                    id: parsedId,
                    form: _result
                  });
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <table className="w-full mt-6">
        <tbody>
          <tr className="content-table-tr">
            <td className="content-table-th">이벤트 여부</td>
            <td className="content-table-td">
              <Switch
                checked={result?.isOnEvent}
                onClick={(_e) => {
                  patchMutation.mutate({
                    id: parsedId,
                    form: {
                      ...result,
                      isOnEvent: !result?.isOnEvent,
                    } as createDeviceForm,
                  });
                }}
              />
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">이벤트 명</td>
            <td className="content-table-td flex-row">
              {result?.eventTag || "-"}
              <img
                onClick={() => {
                  {
                    openModal(MODAL_KEY.TextField, {
                      title: "이벤트 명",
                      click: (change: string) => patchMutation.mutate({
                        id: parsedId,
                        form: {
                          ...result,
                          eventTag: change,
                        } as createDeviceForm,
                      }),
                    });
                  }
                }}
                src="/static/icons/icon_pencil_blue.svg"
                width={24}
                height={24}
                className="ml-[8px] cursor-pointer"
                alt={""}
              />
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">이벤트 내용</td>
            <td className="content-table-td flex-row">
              {result?.eventMessage || "-"}
              <img
                onClick={() => {
                  {
                    openModal(MODAL_KEY.TextField, {
                      title: "이벤트 내용",
                      useTextArea: true,
                      click: (change: string) => patchMutation.mutate({
                        id: parsedId,
                        form: {
                          ...result,
                          eventMessage: change,
                        } as createDeviceForm,
                      }),
                    });
                  }
                }}
                src="/static/icons/icon_pencil_blue.svg"
                width={24}
                height={24}
                className="ml-[8px] cursor-pointer"
                alt={""}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="p-[16px] pt-0 text-[22px]  mt-[47px]">
        색상 옵션 {result?.colorAndImage?.length || 0}
      </h2>
      <div className="flex flex-row justify-start items-center gap-6">
        {isSuccess &&
          (result?.colorAndImage || [])?.map(({ color }, index) => {
            return (
              <div
                key={index}
                className="border border-[rgba(255,255,255, 0.12)] p-[24px] w-full rounded-[8px]"
              >
                <table className="w-full">
                  <tbody>
                    <tr className="content-table-tr">
                      <td className="content-table-th">색상</td>
                      <td className="content-table-td"><p className="whitespace-nowrap">{color}</p></td>
                    </tr>
                  </tbody>
                </table>
                <div className="flex flex-row justify-end items-center mt-[24px]">
                  {/* <Button variant="default" size="md" label="색상 옵션 변경" /> */}
                  <Button variant="outline-red" size="md" label="색상 옵션 삭제" onClick={() => deleteColorMutation.mutate({ id: parsedId, colorName: color })} />
                </div>
              </div>
            );
          })}
      </div>
      <Button
        className="mt-3"
        variant={"outline"}
        label="색상 옵션 추가"
        onClick={() => openModal(MODAL_KEY.TextField, { title: "색상 옵션 추가", click: (change: string) => postColorMutation.mutate({ id: parsedId, colorName: change }) })} />
    </div>
  );
}
